/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'

import {
  useCreateHostedTrafficFilterAssociationMutation,
  useListHostedTrafficFiltersQuery,
} from '@modules/network-security-lib/hooks/hosted'

import { getRegionId } from '@/lib/stackDeployments/selectors/fundamentals' // eslint-disable-line import/no-restricted-paths

import type { TrafficFilterAssociationHandlerProps } from './types'
import type { ForwardRefRenderFunction } from 'react'

const HostedTrafficFilterAssociationHandler: ForwardRefRenderFunction<
  {
    save: () => void
  },
  TrafficFilterAssociationHandlerProps<'hosted'>
> = ({ onClose, deployment, onAssociationChange, onFormStateChange, children }, ref) => {
  // Initialization

  const { data } = useListHostedTrafficFiltersQuery()

  const regionId = getRegionId({ deployment })

  // show only rulesets that match the deployment's region and aren't already associated
  const filteredRulesets = useMemo(
    () =>
      (data?.rulesets || []).filter(
        (ruleset) =>
          ruleset.region === regionId &&
          !ruleset.associations?.some(
            (association) =>
              association.entity_type === 'deployment' && association.id === deployment.id,
          ),
      ),
    [data?.rulesets, regionId, deployment.id],
  )

  // State changes

  const [rulesetId, setRulesetId] = useState('')

  const isSubmittable = useMemo(() => Boolean(rulesetId), [rulesetId])

  // Saving

  const {
    mutate: createTrafficFilterRulesetAssociation,
    isLoading: isSaving,
    error,
  } = useCreateHostedTrafficFilterAssociationMutation()

  const save = () => {
    createTrafficFilterRulesetAssociation(
      {
        rulesetId,
        association: {
          entity_type: 'deployment',
          id: deployment.id,
        },
      },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }

  // Parent API

  useImperativeHandle(ref, () => ({
    save,
  }))

  useEffect(() => {
    onAssociationChange({ isSubmittable })
  }, [isSubmittable])

  useEffect(() => {
    onFormStateChange({ isSaving, error })
  }, [isSaving, error])

  // Rendering

  return children({
    rulesets: filteredRulesets,
    rulesetId,
    setRulesetId,
  })
}

export default forwardRef(HostedTrafficFilterAssociationHandler)
